/**
 * Aurora Customization: This file is necessary to render the PONumberField
 */
import React, { FC, useContext, useMemo } from 'react';
import { Field as FormikField } from 'formik';
import map from 'lodash/map';
import SVG from '@broadleaf/admin-components/dist/common/components/SVG';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import { betweenWith } from '@broadleaf/admin-components/dist/common/utils/lodash';
import { StatusChangeContext } from '@broadleaf/admin-components/dist/oms/contexts';
import type { IFormik } from '@broadleaf/admin-components/dist/types/form';

import auroraMessages from './AuroraStatusChangeFields.messages';
import { Label } from '@broadleaf/admin-components/dist/form';
import { StatusChangeFieldInput } from '@broadleaf/admin-components/dist/oms/elements';
import {
  StatusChangeEstimatedTotalToRefundFieldProps,
  StatusChangeItemQuantityFieldProps,
  StatusChangeOverrideTotalToRefundFieldProps
} from '@broadleaf/admin-components/dist/oms/elements/StatusChangeFields/StatusChangeFields';

export const AuroraStatusChangeFields: FC<
  AuroraStatusChangeFieldsDefinition
> = ({ formik, renderItem, paymentGatewayType, fleetTaxExempt }) => {
  const { items }: { items?: any } = formik.values;
  const { selectAllItems, selectAllItemsMessage, nextStatus } =
    useContext(StatusChangeContext);

  const isAP = isAuroraPay(paymentGatewayType);
  const isCapture = isCapturePayment(nextStatus);
  const isRefund = isConfirmRefund(nextStatus);
  const showAPFields = isAP && (isCapture || isRefund);

  if (selectAllItems) {
    return (
      <div className="StatusChangeFields">
        <p className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-blue-300 tw-bg-blue-100 tw-p-4 tw-text-blue-700">
          <SVG
            className="tw-h-16 tw-w-16 tw-fill-current"
            name="information-solid"
          />
          <span className="tw-ml-5">{selectAllItemsMessage}</span>
        </p>
        {showAPFields && auroraPayFields(formik, fleetTaxExempt, isRefund)}
      </div>
    );
  }

  return (
    <div className="StatusChangeFields">
      {betweenWith(map(items, renderItem(formik)), (_, ___, index) => (
        <hr key={`hr-${index}`} className="tw-my-4 tw-border-gray-300" />
      ))}
      {showAPFields && auroraPayFields(formik, fleetTaxExempt, isRefund)}
    </div>
  );
};

function auroraPayFields(
  formik: IFormik<any>,
  fleetTaxExempt: boolean,
  isRefund: boolean = true
) {
  return (
    <>
      <DealerInvoiceNumberField isReturn={isRefund} />
      <TaxOverrideField
        taxAmount={formik.values.taxOverride}
        fleetTaxExempt={fleetTaxExempt}
      />
      <PONumberField />
    </>
  );
}

function isAuroraPay(paymentGatewayType) {
  return paymentGatewayType === 'CORCENTRIC';
}

function isCapturePayment(nextStatus) {
  return nextStatus === 'CAPTURING_PAYMENT';
}

function isConfirmRefund(nextStatus) {
  return nextStatus === 'CONFIRMED';
}

// Collapsed StatusChangeFieldsDefinition with Aurora fields
export interface AuroraStatusChangeFieldsDefinition {
  formik: IFormik;
  renderItem: Function;
  Quantity?: FC<StatusChangeItemQuantityFieldProps>;
  OverrideTotalToRefund?: FC<StatusChangeOverrideTotalToRefundFieldProps>;
  EstimatedTotalToRefund?: FC<StatusChangeEstimatedTotalToRefundFieldProps>;

  //Aurora Fields
  paymentGatewayType: string;
  fleetTaxExempt: boolean;
}

/*
Aurora Customization: Render an input field for Dealer Invoice Number
 */
const DealerInvoiceNumberField = ({ isReturn = false }) => {
  const formatMessage = useFormatMessage();

  const dealerInvoiceNumberLabel = isReturn
    ? auroraMessages.creditDealerInvoiceNumber
    : auroraMessages.dealerInvoiceNumber;
  const fieldName = isReturn
    ? 'creditDealerInvoiceNumber'
    : 'dealerInvoiceNumber';

  return (
    <>
      <hr className="tw-my-4 tw-border-gray-300" />
      <div className="tw-flex tw-w-full tw-flex-1 tw-flex-row tw-overflow-hidden md:tw-ml-3 md:tw-w-auto">
        <Label
          className={
            'tw-flex-1 tw-overflow-hidden tw-truncate tw-text-lg tw-font-semibold tw-leading-9 tw-text-gray-800'
          }
        >
          <span>{formatMessage(dealerInvoiceNumberLabel)}</span>
        </Label>
        <div className="tw-flex-initial">
          <FormikField
            name={fieldName}
            component={StatusChangeFieldInput}
            className={
              'tw-mr-1 tw-rounded tw-border tw-border-gray-400 tw-px-2 tw-py-1'
            }
          />
        </div>
      </div>
    </>
  );
};

/*
Aurora Customization: Render an input field for Tax Override
 */
const TaxOverrideField = ({ taxAmount, fleetTaxExempt }) => {
  const formatMessage = useFormatMessage();

  return (
    <>
      <hr className="tw-my-4 tw-border-gray-300" />
      <div className="tw-flex tw-w-full tw-flex-1 tw-flex-row tw-overflow-hidden md:tw-ml-3 md:tw-w-auto">
        <Label
          className={
            'tw-flex-1 tw-overflow-hidden tw-truncate tw-text-lg tw-font-semibold tw-leading-9 tw-text-gray-800'
          }
        >
          <span>{formatMessage(auroraMessages.taxOverride)}</span>
        </Label>
        <div className="tw-flex-initial">
          {fleetTaxExempt ? (
            <div className="tw-w-48 tw-rounded tw-border tw-border-gray-300 tw-bg-gray-200 tw-px-4 tw-py-1">
              {taxAmount}
            </div>
          ) : (
            <FormikField
              name={'taxOverride'}
              component={StatusChangeFieldInput}
              className={
                'tw-mr-1 tw-rounded tw-border tw-border-gray-400 tw-px-2 tw-py-1'
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

/*
Aurora Customization: Render an input field for Purchase Order number
 */
const PONumberField = ({ isReturn = false }) => {
  const formatMessage = useFormatMessage();

  const poNumberLabel = isReturn
    ? auroraMessages.creditPurchaseOrderNumber
    : auroraMessages.purchaseOrderNumber;

  return (
    <>
      <hr className="tw-my-4 tw-border-gray-300" />
      <div className="tw-flex tw-w-full tw-flex-1 tw-flex-row tw-overflow-hidden md:tw-ml-3 md:tw-w-auto">
        <Label
          className={
            'tw-flex-1 tw-overflow-hidden tw-truncate tw-text-lg tw-font-semibold tw-leading-9 tw-text-gray-800'
          }
        >
          <span>{formatMessage(poNumberLabel)}</span>
        </Label>
        <div className="tw-flex-initial">
          <FormikField
            name={'purchaseOrderNumber'}
            component={StatusChangeFieldInput}
            className={
              'tw-mr-1 tw-rounded tw-border tw-border-gray-400 tw-px-2 tw-py-1'
            }
          />
        </div>
      </div>
    </>
  );
};

export default AuroraStatusChangeFields;
